/**
 * StudioLayout - Main layout for the kitchen design studio
 *
 * BYK-159: Migrated from floating BentoPanel modals to fixed sidebars
 *
 * New Structure:
 * ┌─────────────────────────────────────────────────────────────┐
 * │                         Toolbar                             │
 * ├─────────────┬─────────────────────────────┬─────────────────┤
 * │ Left        │                             │ Right           │
 * │ Sidebar     │       Center                │ Sidebar         │
 * │ (Create)    │       (Canvas)              │ (Modify)        │
 * ├─────────────┴─────────────────────────────┴─────────────────┤
 * │                       Status Bar                            │
 * └─────────────────────────────────────────────────────────────┘
 */

import { useCallback, useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useFloorPlanStore } from "@/stores/floorPlan";
import {
  useUIStore,
  LEFT_PANEL_CONSTRAINTS,
  RIGHT_PANEL_CONSTRAINTS,
} from "@/stores/uiStore";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useResponsiveLayout } from "@/hooks/useResponsiveLayout";
import { PanelLayout } from "./PanelLayout";
import { LeftSidebar } from "./LeftSidebar";
import { RightSidebar } from "./RightSidebar";
import { FloorPlanCanvas } from "./FloorPlanCanvas";
import { BabylonScene } from "./BabylonScene";
import { Toolbar } from "./Toolbar";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FocusModeIndicator } from "./FocusModeIndicator";
import { SelectionAnnouncer } from "./SelectionAnnouncer";
import { SkipLinks } from "./SkipLinks";
import { CommandPalette } from "./CommandPalette";
import { KeyboardShortcutsHelp } from "./KeyboardShortcutsHelp";
import { MobileDrawer } from "./MobileDrawer";
import { MobileToolbar } from "./MobileToolbar";

// ============================================================================
// Constants
// ============================================================================

const VIEW_TRANSITION_DURATION = 0.35; // seconds

const viewTransitionVariants = {
  initial: {
    opacity: 0,
    scale: 0.98,
    pointerEvents: "none" as const,
  },
  animate: {
    opacity: 1,
    scale: 1,
    pointerEvents: "auto" as const,
    transition: {
      duration: VIEW_TRANSITION_DURATION,
      ease: [0.4, 0, 0.2, 1], // Material Design easing
    },
  },
  exit: {
    opacity: 0,
    scale: 1.02,
    pointerEvents: "none" as const,
    transition: {
      duration: VIEW_TRANSITION_DURATION * 0.8,
      ease: [0.4, 0, 1, 1],
    },
  },
};

// ============================================================================
// Canvas Component (Center Area)
// ============================================================================

function CenterCanvas() {
  const {
    viewMode,
    isViewTransitioning,
    onViewTransitionComplete,
  } = useFloorPlanStore();

  return (
    <main
      id="main-canvas"
      aria-label="Kitchen design canvas"
      className="relative h-full w-full"
    >
      <AnimatePresence
        mode="wait"
        onExitComplete={onViewTransitionComplete}
      >
        {viewMode === "split" ? (
          <motion.div
            key="split-view"
            className="flex h-full"
            variants={viewTransitionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {/* 2D Canvas with Label */}
            <div className="relative w-1/2">
              <FloorPlanCanvas />
              {/* View Label */}
              <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm backdrop-blur-sm">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
                </svg>
                Floor Plan
              </div>
            </div>
            {/* Divider */}
            <div className="relative flex w-1 flex-col items-center justify-center bg-gray-200">
              <div className="absolute h-12 w-1 rounded-full bg-gray-400" />
            </div>
            {/* 3D Scene with Label */}
            <div className="relative flex-1">
              <BabylonScene />
              {/* View Label */}
              <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm backdrop-blur-sm">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
                </svg>
                3D View
              </div>
              {/* Selection Sync Indicator */}
              <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-1.5 rounded-lg bg-blue-50/90 px-2.5 py-1 text-[10px] text-blue-600 shadow-sm backdrop-blur-sm">
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Selection synced
              </div>
            </div>
          </motion.div>
        ) : viewMode === "2d" ? (
          <motion.div
            key="2d-view"
            className="h-full"
            variants={viewTransitionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <FloorPlanCanvas />
          </motion.div>
        ) : (
          <motion.div
            key="3d-view"
            className="h-full"
            variants={viewTransitionVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <BabylonScene />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Transition overlay for extra smoothness */}
      {isViewTransitioning && (
        <motion.div
          className="pointer-events-none absolute inset-0 bg-gray-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          exit={{ opacity: 0 }}
          transition={{ duration: VIEW_TRANSITION_DURATION * 0.5 }}
          aria-hidden="true"
        />
      )}
    </main>
  );
}

// ============================================================================
// Status Bar Component
// ============================================================================

function StatusBar() {
  const { volumes, walls, room } = useFloorPlanStore();

  return (
    <div className="flex h-8 items-center justify-between px-4 text-xs text-gray-500">
      <div className="flex items-center gap-6">
        <span>
          Room: {room.width} × {room.depth} cm
        </span>
        <span>{walls.length} walls</span>
        <span>{volumes.length} products</span>
      </div>
      <div className="flex items-center gap-4">
        <span>
          <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-[10px]">Tab</kbd>{" "}
          Navigate
        </span>
        <span>
          <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-[10px]">Arrow</kbd>{" "}
          Move
        </span>
        <span>
          <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-[10px]">R</kbd>{" "}
          Rotate
        </span>
        <span>
          <kbd className="rounded bg-gray-200 px-1.5 py-0.5 font-mono text-[10px]">Del</kbd>{" "}
          Remove
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// Toolbar Wrapper
// ============================================================================

function ToolbarWrapper() {
  return (
    <div className="flex h-14 w-full items-center px-4 bg-white">
      <ErrorBoundary title="Toolbar Error" showRetry>
        <Toolbar />
      </ErrorBoundary>
    </div>
  );
}

// ============================================================================
// Studio Layout Component
// ============================================================================

export const StudioLayout: React.FC = () => {
  // Command palette state (BYK-163)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  // Keyboard shortcuts help modal state (BYK-166)
  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);

  // Responsive layout (BYK-168)
  const responsive = useResponsiveLayout();

  // Get panel state from UI store
  const {
    leftSidebarCollapsed,
    rightSidebarCollapsed,
    leftSidebarWidth,
    rightSidebarWidth,
    setLeftCollapsed,
    setRightCollapsed,
  } = useUIStore();

  // Track previous breakpoint to detect transitions (fix race condition)
  const prevBreakpointRef = useRef(responsive.breakpoint);

  // Auto-collapse right sidebar only when first transitioning TO tablet mode
  // This prevents overriding user's manual expansion preference
  useEffect(() => {
    const wasDesktop = prevBreakpointRef.current === "desktop";
    const isNowTablet = responsive.isTablet;

    // Only auto-collapse on desktop->tablet transition, not when user manually toggles
    if (wasDesktop && isNowTablet && !rightSidebarCollapsed) {
      setRightCollapsed(true);
    }

    // Update previous breakpoint
    prevBreakpointRef.current = responsive.breakpoint;
  }, [responsive.breakpoint, responsive.isTablet, rightSidebarCollapsed, setRightCollapsed]);

  // Enable keyboard shortcuts for panel operations (BYK-162 & BYK-166)
  useKeyboardShortcuts({
    enabled: !responsive.isMobile, // Disable keyboard shortcuts on mobile
    onCommandPalette: () => setCommandPaletteOpen(true),
    onShowHelp: () => setShortcutsHelpOpen(true),
  });

  // Debounced panel resize for Babylon.js canvas (150ms)
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handlePanelResize = useCallback(() => {
    // Clear previous timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    // Debounce resize event to prevent excessive recalculations
    resizeTimeoutRef.current = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 150);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* WCAG 2.4.1: Skip links for keyboard navigation */}
      <SkipLinks />

      {/* WCAG 2.4.11: Accessibility indicators */}
      <FocusModeIndicator />
      <SelectionAnnouncer />

      {/* Command Palette (BYK-163) */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
      />

      {/* Keyboard Shortcuts Help (BYK-166) */}
      <KeyboardShortcutsHelp
        open={shortcutsHelpOpen}
        onOpenChange={setShortcutsHelpOpen}
      />

      {/* Mobile Layout (BYK-168) */}
      {responsive.isMobile ? (
        <div className="flex h-dvh w-screen flex-col overflow-hidden bg-gray-100">
          {/* Mobile Toolbar */}
          <MobileToolbar
            onOpenLeftDrawer={responsive.openLeftDrawer}
            onOpenRightDrawer={responsive.openRightDrawer}
          />

          {/* Mobile Canvas */}
          <div className="flex-1 overflow-hidden">
            <CenterCanvas />
          </div>

          {/* Mobile Drawers */}
          <MobileDrawer
            open={responsive.leftDrawerOpen}
            onClose={responsive.closeLeftDrawer}
            side="left"
            title="Create"
          >
            <LeftSidebar layout="accordion" />
          </MobileDrawer>

          <MobileDrawer
            open={responsive.rightDrawerOpen}
            onClose={responsive.closeRightDrawer}
            side="right"
            title="Properties"
          >
            <RightSidebar />
          </MobileDrawer>
        </div>
      ) : (
        /* Desktop/Tablet Layout */
        <PanelLayout
          leftSidebar={<LeftSidebar layout="tabs" />}
          center={<CenterCanvas />}
          rightSidebar={<RightSidebar />}
          header={<ToolbarWrapper />}
          footer={<StatusBar />}
          onPanelResize={handlePanelResize}
          leftCollapsed={leftSidebarCollapsed}
          rightCollapsed={rightSidebarCollapsed}
          onLeftCollapsedChange={setLeftCollapsed}
          onRightCollapsedChange={setRightCollapsed}
          defaultLeftSize={leftSidebarWidth}
          defaultRightSize={rightSidebarWidth}
          minLeftSize={LEFT_PANEL_CONSTRAINTS.min}
          minRightSize={RIGHT_PANEL_CONSTRAINTS.min}
          maxLeftSize={LEFT_PANEL_CONSTRAINTS.max}
          maxRightSize={RIGHT_PANEL_CONSTRAINTS.max}
        />
      )}
    </>
  );
};

export default StudioLayout;
